/**
 * OutreachContext - Main Outreach Module Context (TypeScript)
 *
 * Phase 97.8 W2: Instantly fields/hooks/createInstantlyCampaign removed. Reply.io is the only
 * outreach provider after 97.8. Campaigns are listed via the regular `fetchCampaigns` flow.
 */
import React, { createContext, useContext, useCallback, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../lib/queryKeys';

import { useCampaignState } from './useCampaignState';
import { useTemplateState } from './useTemplateState';
import { useContactListState } from './useContactListState';
import type { Template } from '../types/outreach';

interface OutreachProviderProps {
    children: ReactNode;
    isAdmin: boolean;
    onOpenLead?: (leadId: string) => void;
}

// Get return types from hooks
type CampaignStateReturn = ReturnType<typeof useCampaignState>;
type TemplateStateReturn = ReturnType<typeof useTemplateState>;
type ContactListStateReturn = ReturnType<typeof useContactListState>;

interface OutreachContextValue extends CampaignStateReturn {
    // Templates (renamed from hook)
    templates: TemplateStateReturn['templates'];
    templatesLoading: TemplateStateReturn['templatesLoading'];
    templatesError: TemplateStateReturn['templatesError'];
    templateForm: TemplateStateReturn['templateForm'];
    templateSaving: TemplateStateReturn['templateSaving'];
    isCreatingTemplate: TemplateStateReturn['isCreatingTemplate'];
    isTemplateEditing: TemplateStateReturn['isTemplateEditing'];
    isTemplateModalOpen: TemplateStateReturn['isTemplateModalOpen'];
    selectedTemplateId: TemplateStateReturn['selectedTemplateId'];
    setSelectedTemplateId: TemplateStateReturn['setSelectedTemplateId'];
    fetchTemplates: TemplateStateReturn['fetchTemplates'];
    startCreateTemplate: TemplateStateReturn['startCreateTemplate'];
    handleTemplateEdit: TemplateStateReturn['handleTemplateEdit'];
    handleTemplateFieldChange: TemplateStateReturn['handleTemplateFieldChange'];
    handleSaveTemplate: TemplateStateReturn['handleSaveTemplate'];
    handleDeleteTemplate: TemplateStateReturn['handleDeleteTemplate'];
    handleCancelTemplateEdit: TemplateStateReturn['handleCancelTemplateEdit'];

    // Contact Lists
    contactLists: ContactListStateReturn['contactLists'];
    contactListsLoading: ContactListStateReturn['contactListsLoading'];
    contactListOwnerFilter: ContactListStateReturn['contactListOwnerFilter'];
    setContactListOwnerFilter: ContactListStateReturn['setContactListOwnerFilter'];
    contactListUserRole: ContactListStateReturn['contactListUserRole'];
    contactListTeams: ContactListStateReturn['contactListTeams'];
    contactListManagers: ContactListStateReturn['contactListManagers'];
    contactListTeamFilter: ContactListStateReturn['contactListTeamFilter'];
    setContactListTeamFilter: ContactListStateReturn['setContactListTeamFilter'];
    contactListManagerFilter: ContactListStateReturn['contactListManagerFilter'];
    setContactListManagerFilter: ContactListStateReturn['setContactListManagerFilter'];
    contactListsError: ContactListStateReturn['contactListsError'];
    contactListForm: ContactListStateReturn['contactListForm'];
    contactListSaving: ContactListStateReturn['contactListSaving'];
    isCreatingContactList: ContactListStateReturn['isCreatingContactList'];
    isContactListEditing: ContactListStateReturn['isContactListEditing'];
    isContactListModalOpen: ContactListStateReturn['isContactListModalOpen'];
    selectedListId: ContactListStateReturn['selectedListId'];
    setSelectedListId: ContactListStateReturn['setSelectedListId'];
    listDetails: ContactListStateReturn['listDetails'];
    listDetailsLoading: ContactListStateReturn['listDetailsLoading'];
    fetchContactLists: ContactListStateReturn['fetchContactLists'];
    fetchListDetails: ContactListStateReturn['fetchListDetails'];
    startCreateContactList: ContactListStateReturn['startCreateContactList'];
    handleContactListEdit: ContactListStateReturn['handleContactListEdit'];
    handleContactListFieldChange: ContactListStateReturn['handleContactListFieldChange'];
    handleSaveContactList: ContactListStateReturn['handleSaveContactList'];
    handleDeleteContactList: ContactListStateReturn['handleDeleteContactList'];
    handleCancelContactListEdit: ContactListStateReturn['handleCancelContactListEdit'];

    // Template application
    handleApplyTemplateToCampaignForm: () => void;

    // External
    isAdmin: boolean;
    onOpenLead?: (leadId: string) => void;
}

const OutreachContext = createContext<OutreachContextValue | null>(null);

export const OutreachProvider: React.FC<OutreachProviderProps> = ({
    children,
    isAdmin,
    onOpenLead
}) => {
    const qc = useQueryClient();

    // Template state
    const templateState = useTemplateState();

    // Contact list state (campaign refresh via TQ invalidation)
    const contactListState = useContactListState({
        onCampaignRefresh: () => {
            qc.invalidateQueries({ queryKey: queryKeys.outreach.campaigns.all });
        }
    });

    // Campaign state (with fetch callbacks for wizard)
    const campaignState = useCampaignState({
        onTemplatesFetch: templateState.fetchTemplates,
        onContactListsFetch: contactListState.fetchContactLists
    });

    // TQ auto-fetches campaign details when selectedId changes — no manual useEffect needed

    // Template application to campaign form
    const handleApplyTemplateToCampaignForm = useCallback(() => {
        const templateId = campaignState.campaignForm.templateId;
        if (!templateId) return;
        const template = templateState.templates.find((item: Template) => item.id === templateId);
        if (!template) return;
        campaignState.handleCampaignFieldChange('subjectTemplate', template.subject || '');
        campaignState.handleCampaignFieldChange('bodyTemplate', template.body || '');
    }, [campaignState.campaignForm.templateId, templateState.templates, campaignState.handleCampaignFieldChange]);

    // Combined context value
    const value: OutreachContextValue = {
        // Campaigns
        ...campaignState,

        // Templates
        templates: templateState.templates,
        templatesLoading: templateState.templatesLoading,
        templatesError: templateState.templatesError,
        templateForm: templateState.templateForm,
        templateSaving: templateState.templateSaving,
        isCreatingTemplate: templateState.isCreatingTemplate,
        isTemplateEditing: templateState.isTemplateEditing,
        isTemplateModalOpen: templateState.isTemplateModalOpen,
        selectedTemplateId: templateState.selectedTemplateId,
        setSelectedTemplateId: templateState.setSelectedTemplateId,
        fetchTemplates: templateState.fetchTemplates,
        startCreateTemplate: templateState.startCreateTemplate,
        handleTemplateEdit: templateState.handleTemplateEdit,
        handleTemplateFieldChange: templateState.handleTemplateFieldChange,
        handleSaveTemplate: templateState.handleSaveTemplate,
        handleDeleteTemplate: templateState.handleDeleteTemplate,
        handleCancelTemplateEdit: templateState.handleCancelTemplateEdit,

        // Contact Lists
        contactLists: contactListState.contactLists,
        contactListsLoading: contactListState.contactListsLoading,
        contactListOwnerFilter: contactListState.contactListOwnerFilter,
        setContactListOwnerFilter: contactListState.setContactListOwnerFilter,
        contactListUserRole: contactListState.contactListUserRole,
        contactListTeams: contactListState.contactListTeams,
        contactListManagers: contactListState.contactListManagers,
        contactListTeamFilter: contactListState.contactListTeamFilter,
        setContactListTeamFilter: contactListState.setContactListTeamFilter,
        contactListManagerFilter: contactListState.contactListManagerFilter,
        setContactListManagerFilter: contactListState.setContactListManagerFilter,
        contactListsError: contactListState.contactListsError,
        contactListForm: contactListState.contactListForm,
        contactListSaving: contactListState.contactListSaving,
        isCreatingContactList: contactListState.isCreatingContactList,
        isContactListEditing: contactListState.isContactListEditing,
        isContactListModalOpen: contactListState.isContactListModalOpen,
        selectedListId: contactListState.selectedListId,
        setSelectedListId: contactListState.setSelectedListId,
        listDetails: contactListState.listDetails,
        listDetailsLoading: contactListState.listDetailsLoading,
        fetchContactLists: contactListState.fetchContactLists,
        fetchListDetails: contactListState.fetchListDetails,
        startCreateContactList: contactListState.startCreateContactList,
        handleContactListEdit: contactListState.handleContactListEdit,
        handleContactListFieldChange: contactListState.handleContactListFieldChange,
        handleSaveContactList: contactListState.handleSaveContactList,
        handleDeleteContactList: contactListState.handleDeleteContactList,
        handleCancelContactListEdit: contactListState.handleCancelContactListEdit,

        // Template application
        handleApplyTemplateToCampaignForm,

        // External
        isAdmin,
        onOpenLead
    };

    return (
        <OutreachContext.Provider value={value}>
            {children}
        </OutreachContext.Provider>
    );
};

export const useOutreach = (): OutreachContextValue => {
    const context = useContext(OutreachContext);
    if (!context) {
        throw new Error('useOutreach must be used within an OutreachProvider');
    }
    return context;
};

export default OutreachContext;
